import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GeneralAppDataDto } from 'app/api/models/auth/sideBarInfoDto';
import { UserEditPageDto } from 'app/api/models/user/userEditPageDto';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { ROLE_NAMES } from 'app/modules/shared/pipes/translate-roles.pipe';
import { DatalistOption } from 'app/state/abstract/base.repository';
import { toContentLanguage } from 'app/api/models/user/content-language';
import { AuthRepository, UserRoles } from 'app/state/auth.repository';
import { UserGroup } from 'app/state/usergroup.repository';
import { User, UsersRepository } from 'app/state/users.repository';
import {
  UserCreateUpdateDto,
  UsersService,
} from 'app/api/services/users.service';
import { BlobStorageService } from 'app/shared/azure-blob/blob-storage.service';
import { MediaLibraryFileDto } from 'app/api/models/mediaLibrary/mediaLibraryFileDto';
import {
  ContractType,
  EmploymentStatus,
  EmploymentType,
  Shifts,
  WorkLocation,
} from 'app/api/models/employee/employeeEnum';

export const LanguageOptions: DatalistOption[] = [
  { label: 'English', value: 'en' },
  { label: 'Danish', value: 'da' },
];

export const ContentLanguageOptions: DatalistOption[] = [
  { label: 'All', value: -1 },
  { label: 'English', value: 0 },
  { label: 'Danish', value: 1 },
];

@Component({
  selector: 'app-user-basic-info-form',
  templateUrl: './user-basic-info-form.component.html',
  styleUrls: ['./user-basic-info-form.component.scss'],
  standalone: false,
})
export class UserBasicInfoFormComponent implements OnInit {
  readonly ROLE_DESCRIPTIONS = {
    [UserRoles.User]: $localize`:Role description - User:Common User`,
    [UserRoles.TenantAdmin]: $localize`:Role description - Administrator:Can view and edit all data inside a tenant`,
    [UserRoles.Superadmin]: $localize`:Role description - Superadmin:Can edit system data and create tenants`,
    [UserRoles.PartnerManager]: $localize`:Role description - PartnerManager: Partner Manager`,
    [UserRoles.HR]: $localize`:Role description - HR: HR`,
  };

  @Input() set tenants(value: DatalistOption[] | null) {
    if (!value) {
      this.tenantOptions = [];
    } else {
      this.tenantOptions = value;
    }
  }
  @Input() set userGroups(value: UserGroup[] | null) {
    if (!value) {
      this.userGroupsOptions = [];
    } else {
      this.userGroupsOptions = value.map((x) => ({
        value: x.id,
        label: x.name,
      }));
    }
  }
  @Input() mode?: string = '';
  @Input() showPasswordField?: boolean = false;

  @Output() userSubmit = new EventEmitter<UserCreateUpdateDto>();

  ROLE_NAMES = ROLE_NAMES;
  isSuperAdmin?: boolean;
  langOptions: DatalistOption[] | null = LanguageOptions;
  contentLangOptions: DatalistOption[] = ContentLanguageOptions;
  tenantOptions: DatalistOption[] = [];
  userGroupsOptions: DatalistOption[] = [];
  userCoachOptions: DatalistOption[] = [];
  userManagerOptions: DatalistOption[] = [];
  userMenteeOptions: DatalistOption[] = [];
  roleOptions: UserRoles[] = [];
  form!: UntypedFormGroup;
  isActiveTenantSet: boolean = false;
  generalAppData: GeneralAppDataDto = new GeneralAppDataDto();
  activeTenantId = localStorage.getItem('activeTenantId');
  userImageMediaLibraryFile?: MediaLibraryFileDto;
  user: User | undefined;
  id?: string;

  updateValue(control: string, value: any) {
    const controlObject = this.form?.get(control);
    controlObject?.setValue(value);
    controlObject?.markAsTouched();
  }

  public pageData: UserEditPageDto = new UserEditPageDto();

  constructor(
    private formBuilder: UntypedFormBuilder,
    public authRepo: AuthRepository,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    public repo: UsersRepository,
    private generalDataRepo: GeneralDataRepository,
    public blobService: BlobStorageService,
  ) {
    if (this.authRepo.isInRole(UserRoles.Superadmin)) {
      this.roleOptions = Object.values(UserRoles);
    } else if (this.authRepo.isInRole(UserRoles.TenantAdmin)) {
      this.roleOptions = Object.values(UserRoles).filter(
        (r) => r !== UserRoles.Superadmin,
      );
    }
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.id = params.get('id')!;
    });
    this.initForm();
    this.generalDataRepo.allGeneralData$.subscribe((data) => {
      this.generalAppData = data;
    });
    this.isActiveTenantSet = !!localStorage.getItem('activeTenantId');
    if (this.mode == 'new') {
      this.usersService.getUser().subscribe((pageData) => {
        if (pageData) {
          this.setUpForm(pageData);
        }
      });
    }

    if (this.mode == 'edit') {
      this.activatedRoute.paramMap.subscribe((params) => {
        var idFromrout = params.get('id');
        this.usersService.getUser(idFromrout!).subscribe((pageData) => {
          if (pageData) {
            this.setUpForm(pageData);
          }
        });
      });
    }

    if (this.mode == 'profile') {
      this.authRepo.id$.subscribe((id) => {
        this.usersService.getUser(id!).subscribe((pageData) => {
          if (pageData) {
            this.setUpForm(pageData);
          }
        });
      });
    }
  }

  setUpForm(pageData: UserEditPageDto) {
    this.userImageMediaLibraryFile = pageData.user?.imageMediaFile;
    this.userCoachOptions = pageData.coachesList;
    this.userManagerOptions = pageData.managersList;
    this.userMenteeOptions = pageData.menteesList;
    this.pageData = pageData;
    var tenantIdFromLocaleStorage = localStorage.getItem('activeTenantId');
    this.isActiveTenantSet = !!tenantIdFromLocaleStorage;
    const val = pageData.user;
    this.user = val;
    const tenantId = tenantIdFromLocaleStorage
      ? tenantIdFromLocaleStorage
      : null;
    const birthday =
      val && val.birthday ? formatDate(val.birthday, 'yyyy-MM-dd', 'en') : '';
    this.isSuperAdmin = val?.roles[0] === 'Superadmin';
    this.form = this.formBuilder.group({
      name: [val?.name || '', [Validators.required]],
      surname: [val?.surname || ''],
      email: [val?.email, [Validators.required, Validators.email]],
      phone: [val?.phoneNumber || ''],
      imagePath: [val?.imagePath],
      location: [val?.location || ''],
      // -1 is the "All" sentinel. The API serialises the enum by name, so the
      // stored value arrives as "English"/"Danish" and has to be normalised
      // back to the numeric option value or the dropdown shows nothing.
      contentLanguage: [toContentLanguage(val?.contentLanguage) ?? -1],
      language: [val?.language || 'en'],
      jobRole: [val?.jobRole || ''],
      title: [val?.title || ''],
      birthday: [birthday],
      password: ['', this.showPasswordField ? [Validators.required] : []],
      roles: this.formBuilder.array([]),
      // Preselect the active tenant only for a NEW user. When editing, missing
      // tenant data must stay empty (required blocks the save) — substituting
      // the editor's tenant here would overwrite the user's real memberships.
      tenantIds: [
        val?.tenantIds ?? (this.mode === 'new' ? [tenantId] : []),
        [Validators.required],
      ],
      userGroupsIds: [val?.userGroupsIds?.length ? val.userGroupsIds : []],
      userCoachIds: [val?.userCoachIds?.length ? val.userCoachIds : []],
      userManagerIds: [val?.userManagerIds?.length ? val.userManagerIds : []],
      userMenteeIds: [val?.userMenteesIds?.length ? val.userMenteesIds : []],
      isManager: [val?.isManager ?? false],
      isPerformanceTrackingEnabled: val?.isPerformanceTrackingEnabled,
      employmentType: [val?.employmentType ?? null],
      teams: [val?.teams?.length ? val?.teams : []],
      departments: [val?.departments?.length ? val?.departments : []],
      shifts: [val?.shifts ?? null],
      employmentStatus: [val?.employmentStatus ?? null],
      contractType: [val?.contractType ?? null],
      workLocation: [val?.workLocation ?? null],
      employeeNumber: [val?.employeeNumber || ''],
      probationEndDate: [val?.probationEndDate || ''],
      address: [val?.address || ''],
      city: [val?.city || ''],
      zipCode: [val?.zipCode || ''],
      country: [val?.country || ''],
      emergencyContactFullName: [val?.emergencyContactFullName || ''],
      emergencyContactRelationship: [val?.emergencyContactRelationship || ''],
      emergencyContactPhoneNumber: [val?.emergencyContactPhoneNumber || ''],
      spokenLanguages: [
        val?.spokenLanguages?.length ? val?.spokenLanguages : [],
      ],
      notes: [val?.notes || ''],
      tags: [val?.spokenLanguages?.length ? val?.tags : []],
    });
    this.initRolesArray(val?.roles ?? []);
    this.setDefaultRoleUser();
    if (this.mode == 'edit' || this.mode == 'profile') {
      this.form.controls['email'].disable();
    }
    this.loadInitialDropdownData();
  }

  isRoleDisabled(role: UserRoles): boolean {
    return role === UserRoles.User;
  }

  toggleRole(roleIndex: number) {
    const role = this.roleOptions[roleIndex];
    if (this.isRoleDisabled(role)) {
      return;
    }
    const control = this.rolesFormArray.at(roleIndex);
    if (!control || control.disabled) {
      return;
    }
    control.setValue(!control.value);
  }

  setDefaultRoleUser() {
    const rolesArray = this.form.get('roles') as FormArray;
    const roleIndex = this.roleOptions.indexOf(UserRoles.User);

    if (roleIndex !== -1) {
      const roleControl = rolesArray.at(roleIndex);
      roleControl.setValue(true);
      roleControl.disable({ onlySelf: true, emitEvent: false });
    }
  }

  initForm() {
    var tenantIdFromLocaleStorage = localStorage.getItem('activeTenantId');
    const tenantId = tenantIdFromLocaleStorage
      ? tenantIdFromLocaleStorage
      : null;
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      surname: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      imagePath: [''],
      hasCompressedImage: [''],
      location: [''],
      language: ['en'],
      contentLanguage: [-1],
      jobRole: [''],
      title: [''],
      birthday: [''],
      password: ['', this.showPasswordField ? [Validators.required] : []],
      roles: this.formBuilder.array(
        this.roleOptions.map((role) => {
          const isUser = role === UserRoles.User;
          const isChecked =
            isUser ||
            (this.pageData.user &&
              this.pageData.user.roles.indexOf(role) >= 0);

          return this.formBuilder.control({
            value: isChecked,
            disabled: isUser,
          });
        }),
      ),
      tenantIds: [[tenantId], [Validators.required]],
      userGroupsIds: [[]],
      userCoachIds: [[]],
      userManagerIds: [[]],
      userMenteeIds: [[]],
      isCoach: [false],
      isManager: [false],
      isPerformanceTrackingEnabled: [false],
      employmentType: [null],
      employmentStatus: [null],
      contractType: [null],
      workLocation: [null],
      shifts: [null],
      departments: [[]],
      teams: [[]],
      employeeNumber: [''],
      probationEndDate: [''],
      address: [''],
      city: [''],
      zipCode: [''],
      country: [''],
      emergencyContactFullName: [''],
      emergencyContactRelationship: [''],
      emergencyContactPhoneNumber: [''],
      spokenLanguages: [[]],
      notes: [''],
    });
  }

  addRequired(control: AbstractControl | undefined) {
    control?.addValidators(Validators.required);
    control?.updateValueAndValidity();
  }

  removeRequired(control: AbstractControl | undefined) {
    control?.removeValidators(Validators.required);
    control?.updateValueAndValidity();
  }

  onSubmit() {
    // Keep the edited user in the editor's active tenant, but only when the
    // form actually carries tenant data — appending to an empty list would
    // post a fabricated single-tenant list and wipe the user's memberships.
    if (this.activeTenantId && this.form.value.tenantIds?.length) {
      let currentUserTenants = [
        ...new Set([...this.form.value.tenantIds, this.activeTenantId]),
      ];
      this.updateValue('tenantIds', currentUserTenants);
    }

    if (this.mode != 'new') {
      this.removeRequired(this.form?.controls['password']);
    }

    if (this.form && !this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    //temporary solution(when i disable user role for editing js removes it form array, that is why i need to add in manualy as TRUE)
    // const rolesArray = this.form.get('roles') as FormArray;
    // rolesArray.insert(0, new FormControl(true));

    const roles: UserRoles[] = this.rolesFormArray
      .getRawValue()
      .map((checked, i) => (checked ? this.roleOptions[i] : undefined))
      .filter((r): r is UserRoles => r !== undefined);

    if (
      !roles.includes(UserRoles.User) &&
      this.roleOptions.includes(UserRoles.User)
    ) {
      roles.unshift(UserRoles.User);
    }

    this.userSubmit.emit({
      email: this.form.controls['email'].value,
      phoneNumber: this.form?.value?.phone,
      name: this.form?.value?.name,
      surname: this.form?.value?.surname,
      title: this.form?.value?.title,
      location: this.form?.value?.location,
      language: this.form?.value?.language,
      contentLanguage: this.form.value.contentLanguage === -1 ? null : this.form.value.contentLanguage,
      jobRole: this.form?.value?.jobRole,
      birthday: this.form?.value?.birthday || null,
      imagePath: this.form?.value?.imagePath ?? '',
      password: this.form?.value?.password,
      tenantIds: this.form?.value?.tenantIds || null,
      userGroupsIds: this.form?.value?.userGroupsIds || null,
      userCoachIds: this.form?.value?.userCoachIds || null,
      userManagerIds: this.form?.value?.userManagerIds || null,
      userMenteesIds: this.form?.value?.userMenteeIds || [],
      isCoach: this.form?.value.isCoach ?? true,
      isManager: this.form?.value.isManager ?? false,
      roles: roles,
      imageMediaFile: this.userImageMediaLibraryFile,
      isPerformanceTrackingEnabled:
        this.form?.value?.isPerformanceTrackingEnabled ?? false,
      employmentType: this.form.value.employmentType as EmploymentType,
      employmentStatus: this.form.value.employmentStatus as EmploymentStatus,
      contractType: this.form.value.contractType as ContractType,
      workLocation: this.form.value.workLocation as WorkLocation,
      shifts: this.form.value.shifts as Shifts,
      departments: this.form?.value?.departments || null,
      teams: this.form?.value?.teams || null,
      employeeNumber: this.form?.value?.employeeNumber,
      probationEndDate: this.form.value.probationEndDate || null,

      address: this.form?.value?.address,
      city: this.form?.value?.city,
      zipCode: this.form?.value?.zipCode,
      country: this.form?.value?.country,
      emergencyContactFullName: this.form?.value?.emergencyContactFullName,
      emergencyContactRelationship:
        this.form?.value?.emergencyContactRelationship,
      emergencyContactPhoneNumber:
        this.form?.value?.emergencyContactPhoneNumber,
      spokenLanguages: this.form?.value?.spokenLanguages || null,
      notes: this.form?.value?.notes,
      tags: this.form?.value?.tags || null,
    });
    this.form?.markAsUntouched();
  }

  onUserImageMediaLibraryFileSelected(files: MediaLibraryFileDto[]) {
    this.userImageMediaLibraryFile = files[0];
  }

  onUserImageMediaLibraryFileRemoved(file: MediaLibraryFileDto) {
    this.userImageMediaLibraryFile = undefined;
  }

  get rolesFormArray(): FormArray {
    return this.form.get('roles') as FormArray;
  }

  private loadInitialDropdownData() {
    console.time('UsersBundle');

    this.usersService
      .getUserOptionsBundle(this.activeTenantId!)
      .subscribe((res) => {
        this.userCoachOptions = res.coaches;
        this.userManagerOptions = res.managers;
        this.userMenteeOptions = res.mentees;

        console.timeEnd('UsersBundle');
      });
  }

  private normalizeRole(role: string): UserRoles {
    switch (role) {
      case 'TenantAdmin':
        return UserRoles.TenantAdmin;
      case 'Administrator':
        return UserRoles.TenantAdmin;
      default:
        return role as UserRoles;
    }
  }

  private initRolesArray(selectedRoles: UserRoles[]) {
    const normalized = (selectedRoles ?? []).map((r) => this.normalizeRole(r));

    const formArray = this.formBuilder.array(
      this.roleOptions.map((role) => {
        const isUser = role === UserRoles.User;
        const isChecked = isUser || normalized.includes(role);

        return this.formBuilder.control({
          value: isChecked,
          disabled: isUser,
        });
      }),
    );

    this.form.setControl('roles', formArray);
  }
}
